/**
 * Generic skeleton loading screen for the admin and settings pages.
 *
 * One screen serves both: while either loads, what's visible is its chrome —
 * nav bar, title band, and a column of titled sections — and that chrome is
 * the same on each. The moderation and editorial dashboards have their own
 * skeleton in `dashboard-loading-screen.tsx`. The body is kept
 * separate from that chrome, so that an in-page loading state could reuse it
 * alone; `SkeletonAdminScreen` composes the two for a cold load.
 *
 * It replicates `AdminScreen`'s container styles rather than mounting
 * `AdminScreen` itself, which gates on a capability and so needs a datastore
 * that hasn't loaded yet.
 */

import { createThemedStyles } from '@repo/theme/styles';
import { Pad, PadBox, Separator } from 'component/basics';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { NavBarSkeleton } from 'component/progress/nav-bar-skeleton';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { TitledSectionSkeleton } from 'component/progress/titled-section-skeleton';
import { useAdminScreenStyles } from 'component/screen';
import { ScrollView, View } from 'react-native';
import { useIsDesktopBreakpoint } from 'util/layout';

const SECTION_COUNT = 4;

function AdminBodySkeleton() {
    return (
        <View>
            {Array.from({ length: SECTION_COUNT }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={i}>
                    {i > 0 && (
                        <>
                            <Pad size={40} />
                            <Separator />
                            <Pad size={40} />
                        </>
                    )}
                    <TitledSectionSkeleton lines={3} titleWidth={i === 0 ? 200 : 160} />
                </View>
            ))}
        </View>
    );
}

export function SkeletonAdminScreen() {
    const isDesktop = useIsDesktopBreakpoint();
    const admin = useAdminScreenStyles();
    const s = useStyles();

    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <NavBarSkeleton large={isDesktop} />
            <ScrollView style={s.scroller}>
                <View style={admin.header}>
                    <View style={admin.container}>
                        <PadBox vert={40}>
                            <TextContainerSkeleton borderRadius={4} height={28} width={240} />
                        </PadBox>
                    </View>
                </View>
                <View style={admin.container}>
                    <Pad size={40} />
                    <AdminBodySkeleton />
                    <Pad size={80} />
                </View>
            </ScrollView>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
    scroller: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
}));
